import {
  ApiReportGoalAiBaselineDataPredictionStatusChoices,
  ApiReportGoalAiProgressSummaryPredictionStatusChoices,
  ApiReportTargetAiProgressSummaryPredictionStatusChoices,
  ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices,
} from "@api/graphql/types-and-hooks";

/**
 * Resolves a goal-level AI status against the report-wide (treatment plan) one.
 *
 * The goal-level status is the more specific signal, so it wins whenever it has
 * an opinion — FAILURE, PREDICTED, or an in-flight regenerate (PENDING), where
 * showing a stale failure next to "Generating..." would contradict itself. The
 * plan-level status only applies while the goal's own status is still untouched
 * (PENDING / SKIPPED). This matters because the report-wide workflow writes only
 * the plan-level field, and a per-goal regenerate writes only the goal-level one
 * — without this precedence a stale plan-level FAILURE would outlive a
 * successful regenerate forever (PROA-2080).
 */
const resolveFailedByStatus = (
  isGoalFailed: boolean,
  isGoalPredicted: boolean,
  isGoalGenerating: boolean,
  isTreatmentPlanFailed: boolean,
): boolean => {
  if (isGoalFailed) return true;
  if (isGoalPredicted || isGoalGenerating) return false;
  return isTreatmentPlanFailed;
};

const getGoalFieldGenerationStatus = (
  treatmentPlanStatus: ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices | null,
  goalProgressSummaryStatus: ApiReportGoalAiProgressSummaryPredictionStatusChoices | null,
  goalBaselineStatus: ApiReportGoalAiBaselineDataPredictionStatusChoices | null,
): {
  progressSummaryGenerating: boolean;
  progressSummaryFailed: boolean;
  baselineDataGenerating: boolean;
  baselineDataFailed: boolean;
  isTreatmentPlanGenerating: boolean;
  isTreatmentPlanFailed: boolean;
} => {
  const isTreatmentPlanFailed =
    treatmentPlanStatus === ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices.Failure;
  const isTreatmentPlanGenerating =
    treatmentPlanStatus === ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices.Pending ||
    treatmentPlanStatus === ApiReportTreatmentPlanAiGoalProgressSummaryPredictionStatusChoices.GoalPredictionPending;

  const isGoalSummaryGenerating =
    goalProgressSummaryStatus === ApiReportGoalAiProgressSummaryPredictionStatusChoices.GoalPredictionPending;
  const isGoalSummaryFailed =
    goalProgressSummaryStatus === ApiReportGoalAiProgressSummaryPredictionStatusChoices.Failure;
  const isGoalSummaryPredicted =
    goalProgressSummaryStatus === ApiReportGoalAiProgressSummaryPredictionStatusChoices.GoalPredictionPredicted;

  const isGoalBaselineGenerating =
    goalBaselineStatus === ApiReportGoalAiBaselineDataPredictionStatusChoices.GoalPredictionPending;
  const isGoalBaselineFailed = goalBaselineStatus === ApiReportGoalAiBaselineDataPredictionStatusChoices.Failure;
  const isGoalBaselinePredicted =
    goalBaselineStatus === ApiReportGoalAiBaselineDataPredictionStatusChoices.GoalPredictionPredicted;

  return {
    isTreatmentPlanGenerating,
    isTreatmentPlanFailed,
    progressSummaryGenerating: isTreatmentPlanGenerating || isGoalSummaryGenerating,
    progressSummaryFailed: resolveFailedByStatus(
      isGoalSummaryFailed,
      isGoalSummaryPredicted,
      isGoalSummaryGenerating,
      isTreatmentPlanFailed,
    ),
    baselineDataGenerating: isTreatmentPlanGenerating || isGoalBaselineGenerating,
    baselineDataFailed: resolveFailedByStatus(
      isGoalBaselineFailed,
      isGoalBaselinePredicted,
      isGoalBaselineGenerating,
      isTreatmentPlanFailed,
    ),
  };
};

const getTargetFieldGenerationStatus = (
  targetProgressSummaryStatus: ApiReportTargetAiProgressSummaryPredictionStatusChoices | null,
): {
  progressSummaryGenerating: boolean;
  progressSummaryFailed: boolean;
} => {
  // Targets have no report-wide status to reconcile — the target's own status is
  // the only signal, so a successful regenerate clears the error on its own.
  return {
    progressSummaryGenerating:
      targetProgressSummaryStatus === ApiReportTargetAiProgressSummaryPredictionStatusChoices.GoalPredictionPending,
    progressSummaryFailed:
      targetProgressSummaryStatus === ApiReportTargetAiProgressSummaryPredictionStatusChoices.Failure,
  };
};

export { getGoalFieldGenerationStatus, getTargetFieldGenerationStatus };
